import dayjs from 'dayjs'
import { ReactElement, useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Skeleton,
  Typography,
} from '@mui/material'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { Cancel } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchScheduledAssignment,
  fetchLinkedServices,
  fetchCourses,
  fetchLightweightUsersV4,
  selectActiveScheduledAssignment,
  selectActiveScheduledAssignmentStatus,
  selectLinkedServicesStatus,
  selectCourses,
  selectCoursesStatus,
  selectLightweightUsersV4ByService,
  deleteScheduledAssignment,
  updateScheduledAssignment,
  fetchScheduledAssignments,
} from 'src/v3/manage/manage.slice'
import { EditIcon, DeleteIcon } from 'src/v3/core/icons'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import ConfirmationModal from '../components/modals/confirmation-modal'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { dataGridSx } from '../styles'

export default function ScheduledAssignmentDetail(): ReactElement {
  useScrollToTop()

  let { scheduledAssignmentID } = useParams<{ scheduledAssignmentID: string }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { showSuccess, NotificationComponent } = useNotifications()

  let ScheduledAssignmentDetailToolbar = useCallback(
    () => <DataGridToolbar fileName="scheduled-assignment-detail" />,
    [],
  )

  let [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  let [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)

  let scheduledAssignment = useAppSelector(selectActiveScheduledAssignment)
  let scheduledAssignmentStatus = useAppSelector(
    selectActiveScheduledAssignmentStatus,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(
      scheduledAssignment?.linkedServiceID || '',
    ),
  )

  let linkedServiceName = useLinkedServiceName(
    scheduledAssignment?.linkedServiceID,
  )
  let isLoading =
    scheduledAssignmentStatus === 'pending' ||
    linkedServicesStatus === 'pending'

  let handleEdit = () => {
    if (!scheduledAssignment) return
    navigate(`/manage/scheduled-assignments/${scheduledAssignment.id}/edit`)
  }

  let handleDelete = () => {
    if (!scheduledAssignment) return

    dispatch(deleteScheduledAssignment(scheduledAssignment.id))

    if (scheduledAssignment.linkedServiceID) {
      dispatch(fetchScheduledAssignments(scheduledAssignment.linkedServiceID))
    }

    showSuccess('Scheduled assignment deleted successfully')
    setIsDeleteModalOpen(false)
    navigate('/manage/scheduled-assignments')
  }

  let handleCancel = () => {
    if (!scheduledAssignment) return

    let dueDaysAfterGoLive = dayjs(scheduledAssignment.calculatedDueDate).diff(
      dayjs(scheduledAssignment.goLiveDate),
      'day',
    )

    let recurrence = scheduledAssignment.recurrence
      ? {
          ...scheduledAssignment.recurrence,
          recurrenceEndDate:
            scheduledAssignment.recurrence.recurrenceEndDate || undefined,
        }
      : undefined

    let payload = {
      id: scheduledAssignment.id,
      linkedServiceID: scheduledAssignment.linkedServiceID,
      name: scheduledAssignment.name,
      goLiveDate: scheduledAssignment.goLiveDate,
      dueDaysAfterGoLive,
      userIDs: scheduledAssignment.userIDs,
      courseIDs: scheduledAssignment.courseIDs,
      status: 'Cancelled' as const,
      recurrence,
    }

    dispatch(updateScheduledAssignment(payload))

    if (scheduledAssignment.linkedServiceID) {
      dispatch(fetchScheduledAssignments(scheduledAssignment.linkedServiceID))
    }

    showSuccess('Scheduled assignment cancelled successfully')
    setIsCancelModalOpen(false)
    navigate('/manage/scheduled-assignments')
  }

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(coursesStatus, fetchCourses)

  useEffect(() => {
    if (
      scheduledAssignmentID &&
      (scheduledAssignmentStatus === 'uninitialized' ||
        scheduledAssignment?.id !== scheduledAssignmentID)
    ) {
      dispatch(fetchScheduledAssignment(scheduledAssignmentID))
    }
  }, [
    scheduledAssignmentID,
    scheduledAssignmentStatus,
    scheduledAssignment?.id,
    dispatch,
  ])

  useEffect(() => {
    if (scheduledAssignment?.linkedServiceID) {
      dispatch(fetchLightweightUsersV4(scheduledAssignment.linkedServiceID))
    }
  }, [scheduledAssignment?.linkedServiceID, dispatch])

  let getStatusColor = (
    status: 'Pending' | 'Activated' | 'Cancelled' | 'Complete',
  ): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'Pending':
        return 'warning'
      case 'Activated':
        return 'info'
      case 'Complete':
        return 'success'
      case 'Cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  let assignedUsers = lightweightUsers.filter((user) =>
    scheduledAssignment?.userIDs.includes(user.id),
  )

  let assignedCourses = courses.filter((course) =>
    scheduledAssignment?.courseIDs.includes(course.id),
  )

  let userColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: any, row: any) => `${row.firstName} ${row.lastName}`,
    },
    {
      field: 'primaryEmail',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
  ]

  let courseColumns = [
    {
      field: 'name',
      headerName: 'Course Name',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'section',
      headerName: 'Category',
      width: 150,
      valueGetter: (value: any) => value || 'Uncategorized',
    },
    {
      field: 'length',
      headerName: 'Length (hours)',
      width: 130,
      align: 'center' as const,
      headerAlign: 'center' as const,
      valueGetter: (value: any) => value || 0,
    },
  ]

  if (isLoading || !scheduledAssignment) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={400} />
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {NotificationComponent}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mb: 4,
        }}
      >
        <PageTitleWithLinkedService
          pageTitle={scheduledAssignment.name}
          linkedServiceName={linkedServiceName}
          moduleName="Scheduled Assignments"
          handleBackClick={() => navigate('/manage/scheduled-assignments')}
        />
        <Box className="flex flex-wrap gap-4 basis-full md:flex-nowrap md:justify-end md:basis-auto">
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            aria-label="Edit Scheduled Assignment"
          >
            Edit
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            color="warning"
            startIcon={<Cancel />}
            onClick={() => setIsCancelModalOpen(true)}
            aria-label="Cancel Schedule"
            disabled={
              scheduledAssignment.status === 'Complete' ||
              scheduledAssignment.status === 'Cancelled'
            }
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setIsDeleteModalOpen(true)}
            aria-label="Delete Scheduled Assignment"
          >
            Delete
          </Button>
        </Box>
      </Box>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Scheduled Assignment Details
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Status
            </Typography>
            <Chip
              label={scheduledAssignment.status}
              color={getStatusColor(scheduledAssignment.status)}
              size="small"
            />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Go Live Date
            </Typography>
            <Typography variant="body1">
              {dayjs(scheduledAssignment.goLiveDate).format('MMMM D, YYYY')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Due Date
            </Typography>
            <Typography variant="body1">
              {dayjs(scheduledAssignment.calculatedDueDate).format(
                'MMMM D, YYYY',
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ ml: 1 }}
              >
                ({scheduledAssignment.dueDaysAfterGoLive} days after go-live)
              </Typography>
            </Typography>
          </Box>
          {scheduledAssignment.recurrence && (
            <>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Recurrence Type
                </Typography>
                <Chip
                  label={scheduledAssignment.recurrence.recurrenceType}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Recurrence Schedule
                </Typography>
                <Typography variant="body1">
                  {scheduledAssignment.recurrence.recurrenceType === 'Monthly'
                    ? `Every ${scheduledAssignment.recurrence.recurrenceInterval} month${scheduledAssignment.recurrence.recurrenceInterval > 1 ? 's' : ''} on day ${scheduledAssignment.recurrence.dayOfMonth}`
                    : `Every ${scheduledAssignment.recurrence.recurrenceInterval} year${scheduledAssignment.recurrence.recurrenceInterval > 1 ? 's' : ''}`}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Recurrence End Date
                </Typography>
                <Typography variant="body1">
                  {scheduledAssignment.recurrence.recurrenceEndDate
                    ? dayjs(
                        scheduledAssignment.recurrence.recurrenceEndDate,
                      ).format('MMMM D, YYYY')
                    : 'No end date (repeats indefinitely)'}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Card>
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Assigned Users</Typography>
          <Chip
            label={`${assignedUsers.length} user${assignedUsers.length === 1 ? '' : 's'}`}
            color="primary"
          />
        </Box>
        <DataGridPremium
          rows={assignedUsers}
          columns={userColumns}
          slots={{ toolbar: ScheduledAssignmentDetailToolbar }}
          showToolbar
          disableRowSelectionOnClick
          autoHeight
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          sx={dataGridSx}
        />
      </Card>
      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Assigned Courses</Typography>
          <Chip
            label={`${assignedCourses.length} course${assignedCourses.length === 1 ? '' : 's'}`}
            color="secondary"
          />
        </Box>
        <DataGridPremium
          rows={assignedCourses}
          columns={courseColumns}
          slots={{ toolbar: ScheduledAssignmentDetailToolbar }}
          showToolbar
          disableRowSelectionOnClick
          autoHeight
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          sx={dataGridSx}
        />
      </Card>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        entityName="scheduled assignment"
        entityDisplayName={scheduledAssignment.name}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteModalOpen(false)}
      />
      <ConfirmationModal
        isOpen={isCancelModalOpen}
        title="Cancel Schedule"
        onConfirm={handleCancel}
        onClose={() => setIsCancelModalOpen(false)}
      >
        Are you sure you want to cancel the schedule "{scheduledAssignment.name}
        "? This will stop the schedule from creating any future assignments.
        Assignments that have already been created will not be affected.
      </ConfirmationModal>
    </Container>
  )
}
